import { Suspense, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import LocationGoogleMap from 'google-map-react'

// State
import { useThemeContext } from 'src/context/theme'

// Hooks
import { useListingDetailsLocationMapStyles } from 'src/hooks/listings/useMapStyles'

// Components
import { LineScale } from 'src/Components'
import Section from '../Section'
import Marker from './Marker'
import FullScreenButton from './FullScreenButton'

// Styles
import styles from './LocationMap.module.scss'

const LoadingContainer = () => (
  <div className={styles.loadingContainer}>
    <LineScale />
  </div>
)

type ListingProps = { address: { latitude: number; longitude: number } }

interface LocationMapProps {
  listing: ListingProps
  className?: string
  showHeading?: boolean & typeof defaultProps
}

const defaultProps = {
  showHeading: true,
}

const LocationMap = ({ listing, className, showHeading }: LocationMapProps) => {
  const { isLightThemeType } = useThemeContext()
  const listingDetailsLocationMapStyles =
    useListingDetailsLocationMapStyles(isLightThemeType)

  const mapRef = useRef(null)

  const { address: { latitude, longitude } = {} } = listing

  const center = {
    lat: latitude,
    lng: longitude,
  }

  const zoom = 15

  const mapOptions = maps => ({
    styles: listingDetailsLocationMapStyles,
    clickableIcons: false,
    mapTypeControl: false,
    minZoom: 8,
    maxZoom: 18,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP,
    },
    disableDefaultUI: true,
    streetViewControl: false,
    gestureHandling: 'cooperative',
    zoomControl: false,
    fullscreenControl: false,
    rotateControl: false,
  })

  return (
    <Section className={className}>
      {showHeading && (
        <Section.Heading>
          <FormattedMessage
            id='HomeSearch.listingDetails.locationMap'
            defaultMessage='Location'
          />
        </Section.Heading>
      )}
      <Section.Body className={styles.body}>
        <div id='map-container' className={styles.container}>
          <div id='map-wrapper' ref={mapRef} className={styles.wrapper}>
            <Suspense fallback={<LoadingContainer />}>
              <FullScreenButton className={styles.position} />
              <LocationGoogleMap
                yesIWantToUseGoogleMapApiInternals
                bootstrapURLKeys={{
                  key: process.env.REACT_APP_GOOGLE_KEY as string,
                }}
                center={center}
                zoom={zoom}
                options={mapOptions}
              >
                {/* @ts-expect-error: the library definition does not handle lat and lng */}
                <Marker lat={center.lat} lng={center.lng} />
              </LocationGoogleMap>
            </Suspense>
          </div>
        </div>
      </Section.Body>
    </Section>
  )
}

LocationMap.defaultProps = defaultProps

export default LocationMap
